import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, X, Heart, ChatCircle, BookmarkSimple, ShareFat, UserPlus, SpeakerHigh, DownloadSimple, VideoCamera, Lock } from "@phosphor-icons/react";

/**
 * SignupGate — the global "sign up to continue" experience.
 *
 * Anonymous users are allowed to browse the video feed and watch up to
 * `GUEST_VIEW_LIMIT` full-play videos (persisted in localStorage). Every
 * gated action — like, comment, save, share, follow, download, sound,
 * friend-seeker, tip, post, wallet — calls `requireAuth("verb")` from this
 * hook. When the caller is a guest, the modal fires and returns `false` so
 * the caller aborts. When the caller is signed-in, it returns `true`.
 */

const SignupGateContext = createContext(null);

export const GUEST_VIEW_LIMIT = 10;
const STORAGE_KEY = "tain.guest.views";

/** Increment guest view counter and return the new total. */
export function incrementGuestView() {
  try {
    const n = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10) + 1;
    localStorage.setItem(STORAGE_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}
export function getGuestViews() {
  try {
    return parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
  } catch {
    return 0;
  }
}
export function isOverGuestLimit() {
  return getGuestViews() >= GUEST_VIEW_LIMIT;
}

const REASON_COPY = {
  like:        { icon: Heart,          headline: "Like this post?",           body: "Sign up to like posts, follow creators and build your own feed." },
  comment:     { icon: ChatCircle,     headline: "Join the conversation",     body: "Sign up to comment, chat with creators and grow your community." },
  save:        { icon: BookmarkSimple, headline: "Save it for later",         body: "Sign up to bookmark videos and pick up right where you left off." },
  share:       { icon: ShareFat,       headline: "Share the moment",          body: "Sign up to share posts, earn credits and grow your following." },
  follow:      { icon: UserPlus,       headline: "Follow your favourites",    body: "Sign up to follow creators and unlock a fully personalised feed." },
  sound:       { icon: SpeakerHigh,    headline: "Turn on the sound",         body: "Sign up to unlock audio, tip creators and level up your feed." },
  download:    { icon: DownloadSimple, headline: "Download to your device",   body: "Sign up to download videos, follow friends and unlock every feature." },
  friends:     { icon: UserPlus,       headline: "Meet real people",          body: "Sign up to build a Friend-Seeker profile and match with real users near you." },
  watchLimit:  { icon: VideoCamera,    headline: "You've watched a lot!",     body: "Sign up now for unlimited watching, sound, creator tips and referrals." },
  post:        { icon: VideoCamera,    headline: "Ready to create?",          body: "Sign up to post videos, earn tips and build your audience on Tainapp." },
  wallet:      { icon: Sparkle,        headline: "Unlock your wallet",        body: "Sign up to earn credits, tip creators and cash out earnings." },
  default:     { icon: Lock,           headline: "Sign up to continue",       body: "Create your free Tainapp account to unlock everything." },
};

export function SignupGateProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("default");
  const navigate = useNavigate();

  // Poll auth state without importing useAuth here (avoids circular imports).
  // The user object is refreshed via the AuthContext elsewhere; we detect
  // "signed in" by inspecting the session cookie side-effectively via a
  // hidden DOM attribute set from AuthContext consumers, OR we just accept
  // an isAuthed prop from a parent wrapper. Keep it simple: consumers call
  // `requireAuth(isAuthed, verb)`.
  const requireAuth = useCallback((isAuthed, verb = "default") => {
    if (isAuthed) return true;
    setReason(verb in REASON_COPY ? verb : "default");
    setOpen(true);
    return false;
  }, []);

  const close = () => {
    // Hard-lock reasons (guest hit the admin watch threshold) cannot be
    // dismissed — pressing Escape / clicking backdrop is a no-op. The
    // only way past the gate is to sign up (or navigate away entirely).
    if (reason === "watchLimit") return;
    setOpen(false);
  };

  const goSignup = () => {
    setOpen(false);
    navigate("/welcome");
    // /welcome hosts the Google Sign-In CTA.
  };

  const value = useMemo(() => ({ requireAuth, showGate: (verb) => { setReason(verb); setOpen(true); }, close }), [requireAuth]);

  // Global bridge for non-React callers (HlsVideo dispatches this from
  // within its video.js callbacks when the server signals `signup_required`
  // for a guest that's past the admin watch threshold).
  useEffect(() => {
    const onEvent = (e) => {
      const verb = (e && e.detail && e.detail.reason) || "watchLimit";
      setReason(verb in REASON_COPY ? verb : "watchLimit");
      setOpen(true);
    };
    window.addEventListener("tain:signup-gate", onEvent);
    return () => window.removeEventListener("tain:signup-gate", onEvent);
  }, []);

  return (
    <SignupGateContext.Provider value={value}>
      {children}
      <SignupGateModal open={open} reason={reason} onClose={close} onSignup={goSignup} />
    </SignupGateContext.Provider>
  );
}

export function useSignupGate() {
  const ctx = useContext(SignupGateContext);
  if (!ctx) throw new Error("useSignupGate must be used inside SignupGateProvider");
  return ctx;
}

/** Bold, animated sign-up modal. Renders on top of everything.
 * When `hardLock` is true (watchLimit reason), the modal cannot be
 * dismissed by backdrop tap, X button or "Not now" — the only way out
 * is Sign up. Prevents guests from bypassing the gate by tapping-through. */
function SignupGateModal({ open, reason, onClose, onSignup }) {
  const copy = REASON_COPY[reason] || REASON_COPY.default;
  const Icon = copy.icon;
  const hardLock = reason === "watchLimit";

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleBackdrop = () => { if (!hardLock) onClose(); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="signup-gate-modal"
      data-hard-lock={hardLock ? "true" : "false"}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={handleBackdrop}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-[6px] animate-in fade-in duration-200" />

      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[440px] rounded-3xl overflow-hidden
                   bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600
                   shadow-[0_30px_100px_-10px_rgba(251,146,60,0.6)]
                   ring-1 ring-amber-300/50
                   animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300"
      >
        {/* Close — hidden when hardLock */}
        {!hardLock && (
          <button
            onClick={onClose}
            data-testid="signup-gate-close"
            aria-label="Close"
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur grid place-items-center text-white"
          >
            <X size={18} weight="bold" />
          </button>
        )}

        {/* Hero */}
        <div className="pt-9 pb-6 px-6 text-center relative">
          {/* Decorative sparkles */}
          <Sparkle size={22} weight="fill" className="absolute top-6 left-6 text-white/60 animate-pulse" />
          <Sparkle size={16} weight="fill" className="absolute top-12 right-12 text-white/40 animate-pulse" />
          <Sparkle size={14} weight="fill" className="absolute bottom-4 left-14 text-white/40 animate-pulse" />

          <div className="w-20 h-20 mx-auto rounded-full bg-black/25 backdrop-blur-sm grid place-items-center ring-4 ring-white/25 shadow-2xl">
            <Icon size={40} weight="fill" className="text-white drop-shadow-lg" />
          </div>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-black text-white leading-tight tracking-tight drop-shadow-md">
            {copy.headline}
          </h2>
          <p className="mt-3 text-white/95 text-[15px] leading-relaxed font-medium">
            {copy.body}
          </p>
        </div>

        {/* Value props */}
        <div className="px-6 pb-3">
          <div className="rounded-2xl bg-black/25 backdrop-blur-sm p-4 space-y-2.5 text-white text-sm">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-white/25 grid place-items-center text-xs font-black">✓</span>
              <span className="font-medium">Unlimited videos, sound & saves</span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-white/25 grid place-items-center text-xs font-black">✓</span>
              <span className="font-medium">Earn credits from likes, tips & referrals</span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-white/25 grid place-items-center text-xs font-black">✓</span>
              <span className="font-medium">Post your own content & grow followers</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6 pt-3 space-y-2">
          <button
            onClick={onSignup}
            data-testid="signup-gate-cta"
            className="w-full py-4 rounded-2xl bg-white text-black font-heading font-black text-lg
                       shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]
                       transition-all duration-150 tracking-tight"
          >
            Sign up — it's free
          </button>
          {!hardLock && (
            <button
              onClick={onClose}
              data-testid="signup-gate-later"
              className="w-full py-2.5 text-white/80 hover:text-white text-sm font-heading font-medium"
            >
              Not now
            </button>
          )}
          {hardLock && (
            <p className="text-white/70 text-[11px] text-center font-medium pt-1">
              Free preview ended · sign up to keep watching
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
